// monitoring-ui/components/MonitoringUI.tsx
// Shared style constants and small components.
// Mirrors SiteDetailView.tsx card / btn / statusStyle exactly.

import React from 'react';

// ── Shared styles ─────────────────────────────────────────────────────────────

export const card: React.CSSProperties = {
  background: '#111116',
  border: '1px solid #26262f',
  borderRadius: '8px',
  padding: '14px 16px',
};

export const mCard: React.CSSProperties = {
  background: '#111116',
  border: '1px solid #26262f',
  borderRadius: '8px',
  padding: '12px 14px',
};

export const btn = (
  variant: 'primary' | 'secondary' | 'danger' | 'warn' | 'ghost' | 'success' = 'secondary',
): React.CSSProperties => ({
  display: 'inline-flex', alignItems: 'center', gap: '6px',
  padding: '5px 11px', borderRadius: '6px', fontSize: '11.5px',
  fontWeight: 500, cursor: 'pointer', border: 'none',
  fontFamily: "'Inter',sans-serif", transition: 'all .15s', whiteSpace: 'nowrap' as const,
  ...(variant === 'primary'
    ? { background: '#6366f1', color: '#fff' }
    : variant === 'danger'
    ? { background: 'rgba(244,63,94,.1)', color: '#f43f5e', border: '1px solid rgba(244,63,94,.2)' }
    : variant === 'warn'
    ? { background: 'rgba(234,179,8,.1)', color: '#eab308', border: '1px solid rgba(234,179,8,.2)' }
    : variant === 'success'
    ? { background: 'rgba(34,197,94,.1)', color: '#22c55e', border: '1px solid rgba(34,197,94,.2)' }
    : variant === 'ghost'
    ? { background: 'transparent', color: '#8e8ea8', border: '1px solid #26262f' }
    : { background: '#17171e', color: '#eeeef5', border: '1px solid #32323d' }),
});

export const badgeStyle = (
  variant: 'green' | 'amber' | 'red' | 'blue' | 'gray',
): React.CSSProperties => ({
  display: 'inline-flex', alignItems: 'center', gap: '3px',
  padding: '2px 7px', borderRadius: '20px', fontSize: '10.5px', fontWeight: 600,
  ...(variant === 'green'
    ? { background: 'rgba(34,197,94,.1)',   color: '#22c55e', border: '1px solid rgba(34,197,94,.2)' }
    : variant === 'amber'
    ? { background: 'rgba(234,179,8,.1)',   color: '#eab308', border: '1px solid rgba(234,179,8,.2)' }
    : variant === 'red'
    ? { background: 'rgba(244,63,94,.1)',   color: '#f43f5e', border: '1px solid rgba(244,63,94,.2)' }
    : variant === 'blue'
    ? { background: 'rgba(99,102,241,.1)',  color: '#818cf8', border: '1px solid rgba(99,102,241,.2)' }
    : { background: '#17171e', color: '#8e8ea8', border: '1px solid #26262f' }),
});

export const statusVariant = (status: string): 'green' | 'amber' | 'red' => {
  const s = status.toLowerCase();
  if (['completed', 'active', 'running', 'healthy'].includes(s))  return 'green';
  if (['in_progress', 'processing', 'queued', 'warning'].includes(s)) return 'amber';
  return 'red';
};

export const dotColor = (status: string): string => {
  const v = statusVariant(status);
  return v === 'green' ? '#22c55e' : v === 'amber' ? '#eab308' : '#f43f5e';
};

// ── Metric card ───────────────────────────────────────────────────────────────

interface MetricCardProps {
  label: string;
  value: React.ReactNode;
  sub?: React.ReactNode;
  valueColor?: string;
}

export const MetricCard: React.FC<MetricCardProps> = ({ label, value, sub, valueColor }) => (
  <div style={mCard}>
    <div style={{ fontSize: '10px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em', color: '#52525f', marginBottom: '5px' }}>
      {label}
    </div>
    <div style={{ fontSize: '22px', fontWeight: 600, color: valueColor ?? '#eeeef5', lineHeight: 1 }}>
      {value}
    </div>
    {sub && <div style={{ fontSize: '11px', color: '#52525f', marginTop: '4px' }}>{sub}</div>}
  </div>
);

// ── Health bar ────────────────────────────────────────────────────────────────

export const HealthBar: React.FC<{ score: number }> = ({ score }) => {
  const color = score >= 80 ? '#22c55e' : score >= 50 ? '#eab308' : '#f43f5e';
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
      <span style={{ width: '52px', height: '4px', background: '#1a1a22', borderRadius: '2px', overflow: 'hidden', display: 'inline-block', verticalAlign: 'middle' }}>
        <span style={{ display: 'block', width: `${score}%`, height: '4px', background: color, borderRadius: '2px' }} />
      </span>
      <span style={{ fontSize: '11px', color: '#8e8ea8' }}>{score}</span>
    </span>
  );
};

// ── Mono tag ─────────────────────────────────────────────────────────────────

export const Mono: React.FC<{ children: React.ReactNode; color?: string }> = ({ children, color = '#818cf8' }) => (
  <span style={{ fontFamily: "'IBM Plex Mono',monospace", fontSize: '11px', color }}>
    {children}
  </span>
);

// ── Section label ─────────────────────────────────────────────────────────────

export const SectionLabel: React.FC<{ children: React.ReactNode; style?: React.CSSProperties }> = ({ children, style }) => (
  <div style={{ fontSize: '10px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.08em', color: '#52525f', marginBottom: '8px', ...style }}>
    {children}
  </div>
);

// ── AI insight banner ─────────────────────────────────────────────────────────

export const InsightBanner: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div style={{ background: 'rgba(99,102,241,.06)', border: '1px solid rgba(99,102,241,.2)', borderRadius: '8px', padding: '10px 13px', fontSize: '12px', color: '#a5b4fc', display: 'flex', gap: '8px', alignItems: 'flex-start', marginBottom: '12px' }}>
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="#818cf8" strokeWidth="1.5" style={{ flexShrink: 0, marginTop: 1 }} aria-hidden="true">
      <path d="M7 1l1.5 3.5L12 5.5 9.5 8l.5 3.5L7 10l-3 1.5.5-3.5L2 5.5l3.5-1L7 1z"/>
    </svg>
    <div>{children}</div>
  </div>
);

// ── Sparkline bars ────────────────────────────────────────────────────────────

export const Sparkline: React.FC<{ values: number[]; height?: number; color?: string; highlightLast?: boolean }> = ({
  values, height = 52, color = '#6366f1', highlightLast = true,
}) => {
  if (!values.length) return null;

  const max = Math.max(...values, 0.01);
  const min = Math.min(...values, 0);
  const range = max - min || 1;
  const w = 600;
  const h = height;
  const pad = 4;

  const points = values.map((v, i) => {
    const x = (i / (values.length - 1)) * (w - pad * 2) + pad;
    const y = h - pad - ((v - min) / range) * (h - pad * 2);
    return { x, y, v };
  });

  const pathD = points.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' ');

  // Y-axis labels
  const yTicks = [0, 0.5, 1].map(t => ({
    y: h - pad - t * (h - pad * 2),
    label: `$${(min + t * range).toFixed(2)}`,
  }));

  return (
    <div style={{ position: 'relative' }}>
      {/* Y-axis labels */}
      <div style={{ position: 'absolute', left: 0, top: 0, height: h, display: 'flex', flexDirection: 'column', justifyContent: 'space-between', pointerEvents: 'none' }}>
        {[...yTicks].reverse().map((t, i) => (
          <span key={i} style={{ fontSize: '9px', color: '#52525f', lineHeight: 1 }}>{t.label}</span>
        ))}
      </div>

      {/* Chart */}
      <div style={{ marginLeft: '36px' }}>
        <svg width="100%" viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" style={{ display: 'block', height }}>
          {/* Area fill */}
          <path
            d={`${pathD} L${points[points.length - 1].x},${h} L${points[0].x},${h} Z`}
            fill={color}
            opacity={0.08}
          />
          {/* Line */}
          <path d={pathD} fill="none" stroke={color} strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round" />
          {/* Dots on each point */}
          {points.map((p, i) => {
            const isLast = i === points.length - 1;
            const dotColor = highlightLast && isLast ? '#eab308' : color;
            return (
              <circle
                key={i}
                cx={p.x}
                cy={p.y}
                r={isLast && highlightLast ? 4 : 2.5}
                fill={dotColor}
                stroke="#111116"
                strokeWidth="1.5"
              />
            );
          })}
        </svg>
      </div>
    </div>
  );
};

// ── Empty state ───────────────────────────────────────────────────────────────

export const EmptyState: React.FC<{ icon?: string; message: string }> = ({ icon = 'ti-mood-empty', message }) => (
  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '32px 0', color: '#52525f', gap: '8px' }}>
    <i className={`ti ${icon}`} style={{ fontSize: '28px' }} aria-hidden="true" />
    <span style={{ fontSize: '12.5px' }}>{message}</span>
  </div>
);

// ── Loading spinner ───────────────────────────────────────────────────────────

export const Spinner: React.FC = () => (
  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '40px', flexDirection: 'column', gap: '10px' }}>
    <div style={{ width: '24px', height: '24px', borderRadius: '50%', border: '2px solid #26262f', borderTopColor: '#6366f1', animation: 'spin .8s linear infinite' }} />
    <span style={{ fontSize: '12px', color: '#52525f' }}>Loading…</span>
  </div>
);